import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  InputAdornment,
  Tooltip,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Book as BookIcon,
  School as SchoolIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  CloudUpload as PublishIcon,
  Image as ImageIcon,
  People as PeopleIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

import CourseDisplaySettingsPanel from './components/CourseDisplaySettingsPanel';
import CoursePackExportDialog from './components/CoursePackExportDialog';
import CoursePackImportDialog from './components/CoursePackImportDialog';

import axios from 'axios';
import { toast } from 'react-toastify';

// Utility to get admin token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';
const hasCloudinaryConfig = Boolean(CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET);

const uploadThumbnailToCloudinary = async (file) => {
  if (!hasCloudinaryConfig) {
    throw new Error('Cloudinary is not configured. Set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'lms_course_thumbnails');

  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }

  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );

  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) {
    throw new Error('Thumbnail upload failed');
  }
  return secureUrl;
};

const CourseManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [showSyllabusDialog, setShowSyllabusDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingSyllabus, setEditingSyllabus] = useState(null);
  const [selectedCourseCode, setSelectedCourseCode] = useState(null);
  const [syllabus, setSyllabus] = useState({});
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSourceCourse, setDemoSourceCourse] = useState(null);
  const [demoChapters, setDemoChapters] = useState([]);
  const [selectedDemoChapterIds, setSelectedDemoChapterIds] = useState([]);
  const [demoCourseName, setDemoCourseName] = useState('');
  const [exportCourse, setExportCourse] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const [courseForm, setCourseForm] = useState({
    course_name: '',
    fee: '',
    duration_days: '45',
    description: '',
    is_active: true,
    publish_to_app: false,
    thumbnail_url: '',
    show_enrollment_count: true,
  });
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const [syllabusForm, setSyllabusForm] = useState({
    lesson_name: '',
    lesson_order: 0,
  });

  useEffect(() => {
    fetchCourses();
  }, [showInactive]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/students/courses/list?include_inactive=${showInactive}`,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        setCourses(response.data.courses);
        // Fetch syllabus for all courses
        const syllabusPromises = response.data.courses.map(async (course) => {
          try {
            const syllabusResponse = await axios.get(
              `${API_BASE_URL}/students/courses/${course.course_code}/syllabus`,
              { headers: getAuthHeaders() }
            );
            return {
              course_code: course.course_code,
              syllabus: syllabusResponse.data.success ? syllabusResponse.data.syllabus : [],
            };
          } catch (error) {
            return { course_code: course.course_code, syllabus: [] };
          }
        });
        const syllabusData = await Promise.all(syllabusPromises);
        const syllabusMap = {};
        syllabusData.forEach((item) => {
          syllabusMap[item.course_code] = item.syllabus;
        });
        setSyllabus(syllabusMap);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return courses;

    return courses.filter((course) => {
      const haystack = [
        course.course_name,
        course.course_code,
        course.description,
        course.fee != null ? String(course.fee) : '',
        ...(syllabus[course.course_code] || []).map((item) => item.lesson_name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [courses, searchQuery, syllabus]);

  const handleOpenCourseDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setCourseForm({
        course_name: course.course_name,
        fee: course.fee,
        duration_days: course.duration_days,
        description: course.description || '',
        is_active: course.is_active !== undefined ? course.is_active : true,
        publish_to_app: course.publish_to_app === true,
        thumbnail_url: course.thumbnail_url || '',
        show_enrollment_count: course.show_enrollment_count !== false,
      });
    } else {
      setEditingCourse(null);
      setCourseForm({
        course_name: '',
        fee: '',
        duration_days: '45',
        description: '',
        is_active: true,
        publish_to_app: false,
        thumbnail_url: '',
        show_enrollment_count: true,
      });
    }
    setShowCourseDialog(true);
  };

  const openCoursePrice = (event, courseCode) => {
    event.stopPropagation();
    setExpandedCourse(courseCode);
    const focusPrice = (attempt = 0) => {
      const box = document.getElementById(`course-price-${courseCode}`);
      const input = document.getElementById(`course-price-input-${courseCode}`);
      if (box) {
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (input) {
        input.focus();
        return;
      }
      if (attempt < 8) {
        window.setTimeout(() => focusPrice(attempt + 1), 250);
      }
    };
    window.setTimeout(() => focusPrice(), 200);
  };

  const handleCloseCourseDialog = () => {
    setShowCourseDialog(false);
    setEditingCourse(null);
    setCourseForm({
      course_name: '',
      fee: '',
      duration_days: '45',
      description: '',
      is_active: true,
      publish_to_app: false,
      thumbnail_url: '',
      show_enrollment_count: true,
    });
  };

  const handleThumbnailSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const mime = String(file.type || '').toLowerCase();
    if (!mime.startsWith('image/')) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5 MB');
      return;
    }

    try {
      setThumbnailUploading(true);
      const url = await uploadThumbnailToCloudinary(file);
      setCourseForm((prev) => ({ ...prev, thumbnail_url: url }));
      toast.success('Thumbnail uploaded');
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      toast.error(error.message || 'Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleSaveCourse = async () => {
    if (!courseForm.course_name || !courseForm.fee || !courseForm.duration_days) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editingCourse) {
        // Update existing course
        await axios.put(
          `${API_BASE_URL}/students/courses/${editingCourse.course_code}`,
          courseForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Course updated successfully');
      } else {
        // Create new course
        await axios.post(
          `${API_BASE_URL}/students/courses/create`,
          courseForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Course created successfully');
      }
      handleCloseCourseDialog();
      fetchCourses();
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error(error.response?.data?.message || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseCode) => {
    if (!window.confirm('Are you sure you want to delete this course? This will deactivate it.')) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/students/courses/${courseCode}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Course deleted successfully');
      fetchCourses();
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleOpenSyllabusDialog = (courseCode, lesson = null) => {
    setSelectedCourseCode(courseCode);
    if (lesson) {
      setEditingSyllabus(lesson);
      setSyllabusForm({
        lesson_name: lesson.lesson_name,
        lesson_order: lesson.lesson_order || 0,
      });
    } else {
      setEditingSyllabus(null);
      setSyllabusForm({
        lesson_name: '',
        lesson_order: syllabus[courseCode]?.length || 0,
      });
    }
    setShowSyllabusDialog(true);
  };

  const handleCloseSyllabusDialog = () => {
    setShowSyllabusDialog(false);
    setEditingSyllabus(null);
    setSelectedCourseCode(null);
    setSyllabusForm({
      lesson_name: '',
      lesson_order: 0,
    });
  };

  const handleSaveSyllabus = async () => {
    if (!syllabusForm.lesson_name) {
      toast.error('Lesson name is required');
      return;
    }

    try {
      if (editingSyllabus) {
        // Update existing lesson
        await axios.put(
          `${API_BASE_URL}/students/courses/syllabus/${editingSyllabus.id}`,
          syllabusForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Lesson updated successfully');
      } else {
        // Create new lesson
        await axios.post(
          `${API_BASE_URL}/students/courses/${selectedCourseCode}/syllabus`,
          syllabusForm
        );
        toast.success('Lesson added successfully');
      }
      handleCloseSyllabusDialog();
      fetchCourses();
    } catch (error) {
      console.error('Failed to save lesson:', error);
      toast.error(error.response?.data?.message || 'Failed to save lesson');
    }
  };

  const handleDeleteSyllabus = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/students/courses/syllabus/${lessonId}`);
      toast.success('Lesson deleted successfully');
      fetchCourses();
    } catch (error) {
      console.error('Failed to delete lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };

  const handleToggleActive = async (course) => {
    try {
      await axios.put(`${API_BASE_URL}/students/courses/${course.course_code}`, {
        is_active: !course.is_active,
      }, { headers: getAuthHeaders() });
      toast.success(`Course ${course.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchCourses();
    } catch (error) {
      console.error('Failed to toggle course status:', error);
      toast.error('Failed to update course status');
    }
  };

  const handleTogglePublishToApp = async (course) => {
    try {
      const nextValue = course.publish_to_app !== true;
      await axios.put(
        `${API_BASE_URL}/students/courses/${course.course_code}`,
        { publish_to_app: nextValue },
        { headers: getAuthHeaders() },
      );
      toast.success(nextValue ? 'Course published to app' : 'Course unpublished from app');
      fetchCourses();
    } catch (error) {
      console.error('Failed to update app publish status:', error);
      toast.error(error.response?.data?.message || 'Failed to update app publish status');
    }
  };

  const handleOpenDemoDialog = async (course) => {
    try {
      setDemoSourceCourse(course);
      setDemoCourseName(`${course.course_name} Demo`);
      setDemoChapters([]);
      setSelectedDemoChapterIds([]);
      setShowDemoDialog(true);
      setDemoLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/lms/courses/${course.course_code}/builder?syncLegacy=true`,
        { headers: getAuthHeaders() },
      );

      const chapters = response?.data?.chapters || [];
      const normalized = chapters.map((chapter) => ({
        id: chapter.dbId || chapter.id,
        title: chapter.title,
        itemCount: (chapter.items || []).length,
      }));

      setDemoChapters(normalized);
      setSelectedDemoChapterIds(normalized.map((chapter) => chapter.id));
    } catch (error) {
      console.error('Failed to load chapters for demo:', error);
      toast.error(error.response?.data?.message || 'Failed to load chapter list for demo course');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleCloseDemoDialog = () => {
    setShowDemoDialog(false);
    setDemoLoading(false);
    setDemoSubmitting(false);
    setDemoSourceCourse(null);
    setDemoChapters([]);
    setSelectedDemoChapterIds([]);
    setDemoCourseName('');
  };

  const handleToggleDemoChapter = (chapterId) => {
    setSelectedDemoChapterIds((prev) => (
      prev.includes(chapterId)
        ? prev.filter((id) => id !== chapterId)
        : [...prev, chapterId]
    ));
  };

  const handleCreateDemoCourse = async () => {
    if (!demoSourceCourse) {
      return;
    }

    if (!demoCourseName.trim()) {
      toast.error('Demo course name is required');
      return;
    }

    if (selectedDemoChapterIds.length === 0) {
      toast.error('Select at least one chapter for demo course');
      return;
    }

    try {
      setDemoSubmitting(true);
      const response = await axios.post(
        `${API_BASE_URL}/students/courses/${demoSourceCourse.course_code}/demo`,
        {
          demo_course_name: demoCourseName.trim(),
          selected_chapter_ids: selectedDemoChapterIds,
          is_public: true,
        },
        { headers: getAuthHeaders() },
      );

      const demoCourseCode = response?.data?.demo_course?.course_code;
      toast.success(`Demo course created${demoCourseCode ? `: ${demoCourseCode}` : ''}`);
      handleCloseDemoDialog();
      fetchCourses();
    } catch (error) {
      console.error('Failed to create demo course:', error);
      toast.error(error.response?.data?.message || 'Failed to create demo course');
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: '1400px', mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: { xs: 2, sm: 3 },
          p: { xs: 2, sm: 3 },
          mb: 3,
          color: 'white',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1.25, sm: 2 }, minWidth: 0 }}>
            <SchoolIcon sx={{ fontSize: { xs: 32, sm: 40 }, mt: { xs: 0.5, sm: 0 } }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" fontWeight="bold">
                Course Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Manage courses, fees, duration, and syllabus
              </Typography>
            </Box>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Import Course Pack
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCourseDialog()}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                width: { xs: '100%', sm: 'auto' },
                alignSelf: { xs: 'stretch', sm: 'center' },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Add Course
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search courses by name, code, fee, or topic..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 280 } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label="Show Inactive Courses"
              sx={{ flexShrink: 0, mx: 0 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Courses List */}
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <strong>Course price, thumbnail, and enrollment count:</strong> click the green{' '}
        <strong>Price</strong> chip on a course, or open the course and use{' '}
        <strong>Course Price</strong> at the top of Student App Display Settings.
        Direct link pattern: <code>/dashboard/students/courses/COURSE_CODE/display-settings</code>
      </Alert>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : courses.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No courses found. Click "Add Course" to create your first course.
        </Alert>
      ) : filteredCourses.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No courses match &quot;{searchQuery.trim()}&quot;. Try a different search or clear the filter.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {searchQuery.trim() && (
            <Typography variant="body2" color="text.secondary">
              Showing {filteredCourses.length} of {courses.length} course{courses.length === 1 ? '' : 's'}
            </Typography>
          )}
          {filteredCourses.map((course) => (
            <Accordion
              key={course.course_code}
              expanded={expandedCourse === course.course_code}
              onChange={() => setExpandedCourse(expandedCourse === course.course_code ? null : course.course_code)}
              sx={{
                borderRadius: { xs: 1.5, sm: 2 },
                '&:before': { display: 'none' },
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  '& .MuiAccordionSummary-content': {
                    my: { xs: 1.25, sm: 1.5 },
                    minWidth: 0,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', width: '100%', pr: 1.5, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, width: '100%', minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        flexShrink: 0,
                        bgcolor: '#eef2ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {course.thumbnail_url ? (
                        <Box
                          component="img"
                          src={course.thumbnail_url}
                          alt={course.course_name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <BookIcon sx={{ color: course.is_active ? '#4caf50' : '#9e9e9e' }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                        {course.course_name}
                        {!course.is_active && (
                          <Chip label="Inactive" size="small" sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 0.75, sm: 0 } }} color="default" />
                        )}
                        {course.publish_to_app && (
                          <Chip label="Published to App" size="small" sx={{ ml: { xs: 0.75, sm: 1 }, mt: { xs: 0.75, sm: 0 } }} color="success" />
                        )}
                        {/\[Restored\]/i.test(course.course_name || '') && (
                          <Chip label="Restored" size="small" sx={{ ml: { xs: 0.75, sm: 1 }, mt: { xs: 0.75, sm: 0 } }} color="info" />
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          component="span"
                          size="small"
                          icon={<MoneyIcon />}
                          color={Number(course.fee) > 0 ? 'success' : 'default'}
                          variant="outlined"
                          label={Number(course.fee) > 0 ? `Price ₹${Number(course.fee).toLocaleString('en-IN')}` : 'Free'}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => openCoursePrice(e, course.course_code)}
                          sx={{ fontWeight: 700, cursor: 'pointer' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Duration: {course.duration_days} days
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Topics: {syllabus[course.course_code]?.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 16 }} />
                          {Number(course.enrollment_count || 0).toLocaleString()} enrolled
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignSelf: { xs: 'flex-end', sm: 'auto' }, mr: { xs: 3, sm: 0 }, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Tooltip title={course.is_active ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActive(course);
                        }}
                      >
                        {course.is_active ? <CheckCircleIcon color="success" /> : <CancelIcon color="disabled" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={course.publish_to_app ? 'Unpublish from App' : 'Publish to App'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePublishToApp(course);
                        }}
                      >
                        <PublishIcon color={course.publish_to_app ? 'success' : 'disabled'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Course">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCourseDialog(course);
                        }}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Course">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(course.course_code);
                        }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 0, sm: 1 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<SettingsSuggestIcon />}
                        onClick={() => navigate(`/dashboard/students/courses/${course.course_code}/display-settings`)}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Price & Display Settings
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/dashboard/students/courses/${course.course_code}/builder?focus=display`)}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Open in LMS Builder
                      </Button>
                    </Stack>
                    <CourseDisplaySettingsPanel
                      courseCode={course.course_code}
                      compact
                      onFeeSaved={(fee) => {
                        setCourses((prev) => prev.map((item) => (
                          item.course_code === course.course_code ? { ...item, fee } : item
                        )));
                      }}
                    />
                  </Box>

                  {/* Course Description */}
                  {course.description && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Description:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {course.description}
                      </Typography>
                    </Box>
                  )}

                  {/* Syllabus Section */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.25, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Typography variant="h6" fontWeight="bold">
                        Syllabus
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => navigate(`/dashboard/students/courses/${course.course_code}/builder`)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Open LMS Builder
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<SettingsSuggestIcon />}
                          onClick={() => navigate(`/dashboard/students/courses/${course.course_code}/display-settings`)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Price & Display
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDemoDialog(course)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Create Demo Course
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloudDownloadIcon />}
                          onClick={() => setExportCourse(course)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Export Pack
                        </Button>
                      </Stack>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Add quizzes, assignments, flashcards, and coding problems from the LMS Builder or the{' '}
                      <strong>Coding Problems</strong> library.
                    </Typography>

                    {/* Syllabus table and CRUD removed. Use LMS Builder for all content management. */}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Course Dialog */}
      <Dialog
        open={showCourseDialog}
        onClose={handleCloseCourseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 } },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon sx={{ color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold">
              {editingCourse ? 'Edit Course' : 'Add New Course'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Course Name *"
              value={courseForm.course_name}
              onChange={(e) => setCourseForm({ ...courseForm, course_name: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fee (₹) *"
                  type="number"
                  value={courseForm.fee}
                  onChange={(e) => setCourseForm({ ...courseForm, fee: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Duration (Days) *"
                  type="number"
                  value={courseForm.duration_days}
                  onChange={(e) => setCourseForm({ ...courseForm, duration_days: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Description"
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Course Thumbnail
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Recommended 16:9 image (1280×720). Shown on the student app My Learning page.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    width: 160,
                    height: 90,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#f3f4f6',
                    border: '2px dashed',
                    borderColor: courseForm.thumbnail_url ? 'transparent' : '#cbd5e1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {courseForm.thumbnail_url ? (
                    <Box
                      component="img"
                      src={courseForm.thumbnail_url}
                      alt="Course thumbnail preview"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon sx={{ color: '#94a3b8', fontSize: 36 }} />
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={thumbnailUploading ? <CircularProgress size={18} /> : <PublishIcon />}
                    disabled={thumbnailUploading}
                  >
                    {courseForm.thumbnail_url ? 'Change Image' : 'Upload Thumbnail'}
                    <input type="file" hidden accept="image/*" onChange={handleThumbnailSelect} />
                  </Button>
                  {courseForm.thumbnail_url && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => setCourseForm({ ...courseForm, thumbnail_url: '' })}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={courseForm.is_active}
                  onChange={(e) => setCourseForm({ ...courseForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={courseForm.publish_to_app}
                  onChange={(e) => setCourseForm({ ...courseForm, publish_to_app: e.target.checked })}
                />
              }
              label="Publish to LMS Flutter App"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={courseForm.show_enrollment_count}
                  onChange={(e) => setCourseForm({ ...courseForm, show_enrollment_count: e.target.checked })}
                />
              }
              label="Show enrollment count on student app"
            />
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Only active courses published here will appear in the student app course registration home page.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button onClick={handleCloseCourseDialog} variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCourse}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {editingCourse ? 'Update' : 'Create'} Course
          </Button>
        </DialogActions>
      </Dialog>

      {/* Syllabus Dialog */}
      <Dialog
        open={showSyllabusDialog}
        onClose={handleCloseSyllabusDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 } },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookIcon sx={{ color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold">
              {editingSyllabus ? 'Edit Lesson' : 'Add Lesson'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Lesson Name *"
              value={syllabusForm.lesson_name}
              onChange={(e) => setSyllabusForm({ ...syllabusForm, lesson_name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., 1 - FUNDAMENTALS HW VS SW"
              helperText="This lesson will be available for students to select in track sheets"
            />
            <TextField
              label="Order"
              type="number"
              value={syllabusForm.lesson_order}
              onChange={(e) => setSyllabusForm({ ...syllabusForm, lesson_order: parseInt(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Order in which lesson appears (lower numbers appear first)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button onClick={handleCloseSyllabusDialog} variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSyllabus}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {editingSyllabus ? 'Update' : 'Add'} Lesson
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDemoDialog}
        onClose={handleCloseDemoDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 } },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Create Demo Course
          </Typography>
          {demoSourceCourse && (
            <Typography variant="body2" color="text.secondary">
              Source: {demoSourceCourse.course_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Demo Course Name"
              value={demoCourseName}
              onChange={(e) => setDemoCourseName(e.target.value)}
              fullWidth
              required
            />

            {demoLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight="bold">
                  Select Chapters for Demo
                </Typography>

                {demoChapters.length === 0 ? (
                  <Alert severity="warning">No chapters found in source course.</Alert>
                ) : (
                  <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
                    {demoChapters.map((chapter) => (
                      <FormControlLabel
                        key={chapter.id}
                        control={(
                          <Checkbox
                            checked={selectedDemoChapterIds.includes(chapter.id)}
                            onChange={() => handleToggleDemoChapter(chapter.id)}
                          />
                        )}
                        label={`${chapter.title} (${chapter.itemCount} items)`}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button onClick={handleCloseDemoDialog} disabled={demoSubmitting} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDemoCourse}
            disabled={demoLoading || demoSubmitting || demoChapters.length === 0}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {demoSubmitting ? 'Creating...' : 'Create Demo'}
          </Button>
        </DialogActions>
      </Dialog>

      <CoursePackExportDialog
        open={Boolean(exportCourse)}
        onClose={() => setExportCourse(null)}
        courseCode={exportCourse?.course_code}
        courseName={exportCourse?.course_name}
      />

      <CoursePackImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImported={(restored) => {
          fetchCourses();
          if (restored?.course_code) {
            navigate(`/dashboard/students/courses/${restored.course_code}/builder`);
          }
        }}
      />
    </Box>
  );
};

export default CourseManagement;

